import type React from "react";
import { useRef, useState } from "react";

export interface HandleProps {
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerCancel: (e: React.PointerEvent<HTMLDivElement>) => void;
    style: { cursor: string; touchAction: "none"; userSelect: "none" };
}

interface PanelSize {
    width: number;
    height: number;
}

interface UsePanelResizeOptions {
    maxWidth: number;
    defaultHeight: number;
}

interface UsePanelResizeResult {
    size: PanelSize;
    heightHandleProps: HandleProps;
    widthHandleProps: HandleProps;
}

const MIN_HEIGHT = 100;
const MIN_WIDTH = 250;

function clampWidth(value: number, maxWidth: number): number {
    if (maxWidth <= 0) return Math.max(MIN_WIDTH, value);
    return Math.max(MIN_WIDTH, Math.min(maxWidth, value));
}

function effectiveWidth(preferredWidth: number | null, maxWidth: number): number {
    if (preferredWidth !== null) return clampWidth(preferredWidth, maxWidth);
    return maxWidth > 0 ? maxWidth : MIN_WIDTH;
}

// Enhance a panel with mouse drag listening props you can pass to a React drag handle, and height/width
// dimensions that can be passed to the EChart resize method
export function usePanelResize({ maxWidth, defaultHeight }: UsePanelResizeOptions): UsePanelResizeResult {
    const [preferredWidth, setPreferredWidth] = useState<number | null>(null);
    const [preferredHeight, setPreferredHeight] = useState<number | null>(null);

    const width = effectiveWidth(preferredWidth, maxWidth);
    const height = preferredHeight ?? defaultHeight;

    const maxWidthRef = useRef(maxWidth);
    maxWidthRef.current = maxWidth;
    const widthRef = useRef(width);
    widthRef.current = width;
    const heightRef = useRef(height);
    heightRef.current = height;

    const dragAxis = useRef<"x" | "y" | null>(null);
    const dragStartPos = useRef(0);
    const dragStartSize = useRef(0);

    const heightCursor = height <= MIN_HEIGHT ? "s-resize" : "ns-resize";
    let widthCursor = "ew-resize";
    if (width <= MIN_WIDTH) widthCursor = "e-resize";
    else if (maxWidth > 0 && width >= maxWidth) widthCursor = "w-resize";

    const endDrag = () => {
        dragAxis.current = null;
    };

    const makeHandleProps = (axis: "x" | "y"): HandleProps => ({
        onPointerDown: (e) => {
            if (e.pointerType === "touch") e.preventDefault();
            dragAxis.current = axis;
            e.currentTarget.setPointerCapture(e.pointerId);
            dragStartPos.current = axis === "y" ? e.clientY : e.clientX;
            dragStartSize.current = axis === "y" ? heightRef.current : widthRef.current;
        },
        onPointerMove: (e) => {
            if (dragAxis.current !== axis) return;
            e.preventDefault();
            const pos = axis === "y" ? e.clientY : e.clientX;
            const newSize = dragStartSize.current + (pos - dragStartPos.current);
            if (axis === "y") {
                setPreferredHeight(Math.max(MIN_HEIGHT, newSize));
            } else {
                setPreferredWidth(clampWidth(newSize, maxWidthRef.current));
            }
        },
        onPointerUp: endDrag,
        onPointerCancel: endDrag,
        style: {
            cursor: axis === "y" ? heightCursor : widthCursor,
            touchAction: "none",
            userSelect: "none",
        },
    });

    return {
        size: { width, height },
        heightHandleProps: makeHandleProps("y"),
        widthHandleProps: makeHandleProps("x"),
    };
}
